/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import cx from 'classnames';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {formatAutoCleanPhoneNumber, pluralize} from '~/common/utils';
import {VolunteerLinkPreview} from '~/SignUpSheets/components/LinkPreview/VolunteerLinkPreview';
import {useAffiliateActions} from '~/SignUpSheets/hooks/useAffiliateActions';
import {useRemoveSignup} from '~/SignUpSheets/hooks/useRemoveSignup';
import {useSaveOrUpdateSignups} from '~/SignUpSheets/hooks/useSaveOrUpdateSignups';
import {selectSignupCommentMaxLength} from '~/SignUpSheets/reducers/blueprint/selectors';
import {
  SET_SELECTED_SIGNUPS,
  SET_SIGNUPS,
  SET_SIGNUPS_PENDING_DELETION,
} from '~/SignUpSheets/reducers/sheet/constants';
import {
  selectIsOrganizer,
  selectIsPhoneRequired,
  selectOrganizerName,
  selectSelectedSignups,
  selectSheetType,
  selectShowPhoneField,
  selectSignupOptionGroups,
  selectSignupOptions,
  selectSignupQuestions,
  selectSignups,
  selectSignupsPendingDeletion,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {
  getGroupTitle,
  isAllDay,
  isSpecificSignup,
  resizeTextarea,
  sortGroups,
} from '~/SignUpSheets/utils/misc';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {TrashIcon} from '~library/svg/TrashIcon';
import {sanitizeString} from '~library/utils/sanitize_html';
import {pebble} from '~libSass/base/_exports.sass';
import {VolunteerSignupOverlayCounter} from './VolunteerSignupOverlayCounter';
import './SignUpOverlay.sass';

export const SignUpOverlay = ({
  selectedSignups,
  closeOverlay,
  isOpen,
  isEditing,
  legacyDateTime,
  isDesktop,
  currentUserId,
}) => (
  <ResponsivePanel
    isOpen={isOpen}
    onClose={closeOverlay}
    title={`${isEditing ? 'Edit' : 'Confirm'} ${pluralize('signup', selectedSignups.length)}`}
    modalId="volunteer-signup"
    closeQaId="volunteer-signup"
    showCover
    backArrow={!isDesktop}
  >
    <div className="one-step-flow__overlay-body">
      <OverlayContent
        closeOverlay={closeOverlay}
        isEditing={isEditing}
        isOpen={isOpen}
        legacyDateTime={legacyDateTime}
        isDesktop={isDesktop}
        currentUserId={currentUserId}
      />
    </div>
  </ResponsivePanel>
);

const OverlayContent = ({
  closeOverlay,
  isEditing,
  isOpen,
  legacyDateTime,
  isDesktop,
  currentUserId,
}) => {
  const dispatch = useDispatch();
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [signupError, setSignupError] = useState({});
  const [questionError, setQuestionError] = useState('');
  const [volunteerName, setVolunteerName] = useState('');
  const [volunteerEmail, setVolunteerEmail] = useState('');
  const [volunteerPhone, setVolunteerPhone] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [selectedSignupsToShow, setSelectedSignupsToShow] = useState([]);
  const signupOptions = useSelector(selectSignupOptions, shallowEqual);
  const groups = useSelector(selectSignupOptionGroups);
  const selectedSignups = useSelector(selectSelectedSignups, shallowEqual);
  const signupsPendingDeletion = useSelector(selectSignupsPendingDeletion, shallowEqual);
  const {linkPreviews} = useAffiliateActions();
  const signups = useSelector(selectSignups, shallowEqual);
  const isOrganizer = useSelector(selectIsOrganizer);
  const organizerName = useSelector(selectOrganizerName);
  const showPhoneField = useSelector(selectShowPhoneField);
  const isPhoneRequired = useSelector(selectIsPhoneRequired);
  const {onSave} = useSaveOrUpdateSignups({
    volunteerName,
    volunteerEmail,
    volunteerPhone,
    isEditing,
    showPhoneField,
    setNameError,
    setEmailError,
    setPhoneError,
    setQuestionError,
    setSignupError,
  });
  const [handleRemoveSignup] = useRemoveSignup(closeOverlay);
  const signedInEmail = useSelector((state) => state.user.email);
  const userName = useSelector((state) => state.user.userName);
  const signedInName = signups.find((s) => s.user_id === currentUserId)?.name || userName;
  const signedInPhone = signups.find((s) => s.user_id === currentUserId)?.phone || '';

  const dispatchSelectedSignups = useCallback(
    (signupsToSubmit) => {
      dispatch({
        type: SET_SELECTED_SIGNUPS,
        payload: signupsToSubmit,
      });
    },
    [dispatch]
  );

  const dispatchSignupsPendingDeletion = useCallback(
    (signupsToDelete) => {
      dispatch({
        type: SET_SIGNUPS_PENDING_DELETION,
        payload: signupsToDelete,
      });
    },
    [dispatch]
  );

  const handleTrashCanClick = useCallback(
    (item) => {
      const isFromOtherSignupOrOtherUser = (f) =>
        // Hold onto others' signups, as well as your signups for different slots, in the UI
        (f.slot_id ? f.slot_id !== item.slot_id : f.signup_option !== item.signup_option) ||
        (f.user_id !== '' && f.user_id !== currentUserId);

      const signupsToRemove = signups.filter((f) => !isFromOtherSignupOrOtherUser(f));
      signupsToRemove.forEach((signup) => {
        if (!signup.id) return; // Not a saved signup yet; nothing to remove on BE
        handleRemoveSignup(signup);
      });

      const signupsToKeep = signups.filter((f) => isFromOtherSignupOrOtherUser(f));
      dispatch({type: SET_SIGNUPS, payload: signupsToKeep});

      const selectedSignupsToKeep = selectedSignups.filter((f) => isFromOtherSignupOrOtherUser(f));
      dispatchSelectedSignups(selectedSignupsToKeep);

      const signupsPendingDeletionToKeep = signupsPendingDeletion.filter((f) =>
        isFromOtherSignupOrOtherUser(f)
      );
      dispatchSignupsPendingDeletion(signupsPendingDeletionToKeep);

      if (selectedSignupsToKeep.length === 0) {
        closeOverlay();
      }
    },
    [
      signups,
      dispatch,
      selectedSignups,
      dispatchSelectedSignups,
      signupsPendingDeletion,
      dispatchSignupsPendingDeletion,
      currentUserId,
      handleRemoveSignup,
      closeOverlay,
    ]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setVolunteerName(isEditing || signedInName ? signedInName : '');
    setVolunteerEmail(isEditing || signedInEmail ? signedInEmail : '');
    setVolunteerPhone(isEditing || signedInPhone ? signedInPhone : '');
  }, [isOpen, isEditing, signedInName, signedInEmail, signedInPhone]);

  const addSignups = useCallback(
    (selectedItem, count) => {
      const selectedSignupsForSlot = selectedSignups.filter((s) =>
        isSpecificSignup(s, selectedItem)
      );
      let numToAdd = count - selectedSignupsForSlot.length;
      const slotsPendingDeletion = [...signupsPendingDeletion];
      const selected = [...selectedSignups];
      while (numToAdd > 0) {
        const idxOfSlotToRestore = slotsPendingDeletion.findIndex((s) =>
          isSpecificSignup(s, selectedItem)
        );
        const slotInsertIdx = selected.findIndex((s) => isSpecificSignup(s, selectedItem));
        if (idxOfSlotToRestore >= 0) {
          // There is an existing slot we can restore
          const slotToRestore = slotsPendingDeletion.splice(idxOfSlotToRestore, 1);
          selected.splice(Math.max(slotInsertIdx, 0), 0, ...slotToRestore); // Math.max accounts for possibly negative insertIdx
        } else {
          // This is a completely new slot
          selected.splice(Math.max(slotInsertIdx, 0), 0, {
            // Math.max accounts for possibly negative insertIdx
            ...selectedItem,
            id: undefined,
          });
        }
        numToAdd--;
      }
      dispatchSelectedSignups(selected);
      dispatchSignupsPendingDeletion(slotsPendingDeletion);
    },
    [
      signupsPendingDeletion,
      selectedSignups,
      dispatchSelectedSignups,
      dispatchSignupsPendingDeletion,
    ]
  );

  const removeSignups = useCallback(
    (selectedItem, count) => {
      const selectedSignupsForSlot = selectedSignups.filter((s) =>
        isSpecificSignup(s, selectedItem)
      );
      let numToRemove = selectedSignupsForSlot.length - count;
      if (count === 0) {
        handleTrashCanClick(selectedSignupsForSlot);
        return;
      }

      const slotsToDelete = [...signupsPendingDeletion];
      const selected = [
        ...selectedSignups.sort((a, b) => {
          if (!isSpecificSignup(a, b)) return 0; // Don't sort across signup options, just within
          if (!a.id && b.id) return -1;
          if (!b.id && a.id) return 1;
          if (a.id && b.id) return b.id - a.id;
          return 0;
        }),
      ];
      while (numToRemove > 0) {
        let idx = selected.findIndex((s) => isSpecificSignup(s, selectedItem) && !s.comment);
        if (idx < 0) idx = selected.findIndex((s) => isSpecificSignup(s, selectedItem));
        if (idx >= 0) {
          const existingSlotToDelete = selected.splice(idx, 1);
          if (existingSlotToDelete[0].id) {
            // Previously saved signup; stage for deletion but don't remove until user has clicked Save
            // Push to front of list
            slotsToDelete.splice(0, 0, existingSlotToDelete[0]);
          }
        }
        numToRemove--;
      }
      dispatchSelectedSignups(selected);
      dispatchSignupsPendingDeletion(slotsToDelete);
    },
    [
      selectedSignups,
      signupsPendingDeletion,
      dispatchSelectedSignups,
      dispatchSignupsPendingDeletion,
      handleTrashCanClick,
    ]
  );

  const adjustQuantity = useCallback(
    (signupOption, type, selectedToAdjust, count) => {
      const selectedItem = selectedToAdjust.find((s) => isSpecificSignup(s, signupOption));

      if (selectedItem) {
        switch (type) {
          case 'add': {
            addSignups(selectedItem, count);
            break;
          }
          case 'subtract': {
            removeSignups(selectedItem, count);
            break;
          }
          default: {
            break;
          }
        }
      }
      return selectedToAdjust;
    },
    [addSignups, removeSignups]
  );

  const updateSelectedSignupsToShow = useCallback(
    (allSelectedSignups) => {
      const mapGroups = {};

      const groupedSignups = allSelectedSignups.reduce((acc, s) => {
        const existingSignup = acc.find((item) => isSpecificSignup(item, s));

        if (existingSignup) {
          existingSignup.selected_quantity++;
        } else {
          acc.push({...s, selected_quantity: 1});
        }
        return acc;
      }, []);

      const formattedSignupsToShow = groupedSignups.map((signup, index) => {
        const so = signupOptions.find((s) => s.uuid === signup.signup_option);
        if (so.uuid in mapGroups) {
          mapGroups[so.uuid] = Math.min(index, mapGroups[so.uuid]);
        } else {
          mapGroups[so.uuid] = index;
        }
        return {
          ...signup,
          so,
          group: groups?.[so.group] ?? null,
          index: mapGroups[so.uuid],
        };
      });

      setSelectedSignupsToShow(
        [...formattedSignupsToShow].sort((a, b) => {
          if (a.group && b.group) return sortGroups(a, b);
          if (a.start_time) {
            return (
              new Date(`${a.so?.date} ${a.start_time}`) - new Date(`${b.so?.date} ${b.start_time}`)
            );
          }
          if (a.so?.date) {
            return new Date(a.so?.date) - new Date(b.so?.date);
          }
          if (a.signup_option === b.signup_option) {
            return a.id - b.id;
          }
          return a.index - b.index;
        })
      );
    },
    [signupOptions, groups]
  );

  useEffect(() => {
    updateSelectedSignupsToShow(selectedSignups);
  }, [selectedSignups, updateSelectedSignupsToShow]);

  return (
    <div className="sign-up-overlay__content">
      <Input
        className="sign-up-overlay__your-name"
        data-qa-id="sign-up-overlay-your-name"
        type="text"
        label="Your Name"
        required
        value={volunteerName}
        disabled={isEditing}
        onChange={(e) => setVolunteerName(e.target.value)}
        placeholder="Enter your name"
        error={nameError}
      />
      <Input
        className="sign-up-overlay__your-email"
        data-qa-id="sign-up-overlay-your-email"
        type="text"
        label="Your Email Address"
        required
        value={volunteerEmail}
        disabled={isEditing}
        onChange={(e) => setVolunteerEmail(e.target.value)}
        placeholder="Enter your email address"
        error={emailError}
      />
      {showPhoneField && (
        <Input
          className="sign-up-overlay__your-phone"
          data-qa-id="sign-up-overlay-your-phone"
          type="text"
          label="Phone Number"
          value={formatAutoCleanPhoneNumber(volunteerPhone)}
          disabled={isEditing}
          onChange={(e) => setVolunteerPhone(formatAutoCleanPhoneNumber(e.target.value))}
          placeholder="(123) 456-7890"
          error={phoneError}
          required={isPhoneRequired}
        />
      )}
      {selectedSignups.length > 0 && (
        <div className="signup-up-overlay__sign-up-items">
          {selectedSignupsToShow.map((selected) => {
            const productLink = selected.slot_id
              ? {
                  ...(selected.so.slots.find((slot) => slot.slot_id === selected.slot_id)
                    ?.product_link ?? {}),
                  ...linkPreviews[selected.slot_id],
                }
              : {
                  ...(selected.so.product_link ?? {}),
                  ...linkPreviews[selected.signup_option],
                };
            return (
              <SignUpItemForm
                key={`${selected.signup_option}__${selected.slot_id}`}
                selected={{
                  ...selected,
                  productLink,
                }}
                questionError={questionError}
                signupError={signupError}
                legacyDateTime={legacyDateTime}
                selectedSignups={selectedSignups}
                selectedSignupsToShow={selectedSignupsToShow}
                updateSelectedSignupsToShow={updateSelectedSignupsToShow}
                adjustQuantity={adjustQuantity}
                handleTrashCanClick={handleTrashCanClick}
                isEditing={isEditing}
                isOrganizer={isOrganizer}
                organizerName={organizerName}
                isDesktop={isDesktop}
                currentUserId={currentUserId}
                hasSubmitted={hasSubmitted}
              />
            );
          })}
        </div>
      )}
      <Button
        className="sign-up-overlay__save-btn"
        data-qa-id="sign-up-overlay-save-btn"
        variant="primary"
        onClick={() => {
          setHasSubmitted(true);
          onSave();
        }}
        fullWidth
        size="large"
      >
        Save
      </Button>
    </div>
  );
};

const SignUpItemForm = ({
  selected,
  signupError,
  legacyDateTime,
  selectedSignups,
  selectedSignupsToShow,
  updateSelectedSignupsToShow,
  adjustQuantity,
  handleTrashCanClick,
  isDesktop,
  isOrganizer,
  organizerName,
  currentUserId,
  questionError,
  hasSubmitted,
}) => {
  const {
    signup_option: signupOption,
    comment,
    productLink,
    slot_id: slotId,
    group,
    answers: initialAnswers,
  } = selected;
  const item = selected.so;
  const slot = item?.slots?.find((s) => selected.slot_id === s.slot_id);
  const slotTitle = slot?.title || item?.item;
  const slotDescription = slot?.description;
  const allDay = isAllDay(selected.start_time, selected.end_time);
  const commentRef = useRef(null);
  const signupCommentMaxLength = useSelector(selectSignupCommentMaxLength);
  const sheetType = useSelector(selectSheetType);
  const questions = useSelector(selectSignupQuestions);
  const [answers, setAnswers] = useState(initialAnswers || []);
  const [volunteerComment, setVolunteerComment] = useState(comment);
  const dispatch = useDispatch();

  useEffect(() => {
    setAnswers(
      questions.map((q) => {
        const existingAnswer = initialAnswers?.find((a) => a.question_id === q.uuid);
        return {
          question_id: q.uuid,
          label: q.title,
          answer: existingAnswer?.answer || '',
        };
      })
    );
  }, [questions, initialAnswers]);

  let errorMsg = '';
  if (slotId) {
    errorMsg = signupError.slot_id === slotId ? signupError.message : '';
  } else {
    errorMsg = signupError.signup_option === signupOption ? signupError.message : '';
  }

  useEffect(() => {
    resizeTextarea(commentRef.current);
  }, []);
  const groupTitle = getGroupTitle(group);

  const handleAnswerChange = useCallback(
    (questionId, value) =>
      setAnswers((prev) =>
        prev.map((i) => (i.question_id === questionId ? {...i, answer: value} : i))
      ),
    []
  );

  const signupTitle = useMemo(() => {
    if (!allDay) {
      return (
        <div className="sign-up-overlay__item-date-time">
          {groupTitle && (
            <Typography variant="header5" data-qa-id="group-name">
              {groupTitle}
            </Typography>
          )}
          <Typography variant={groupTitle ? 'paragraph2' : 'header5'} data-qa-id="item-date">
            {slotTitle || `${selected.start_time} - ${selected.end_time}`}
          </Typography>
          {selected.start_time && selected.end_time && slotTitle ? (
            <Typography variant="list1" size="large" data-qa-id="item-time-range">
              {selected.start_time} - {selected.end_time}
            </Typography>
          ) : null}
        </div>
      );
    }
    return (
      <div className="sign-up-overlay__item-date-time">
        {slotTitle && (
          <Typography
            variant="header5"
            className="sign-up-overlay__item-name"
            data-qa-id="item-name"
          >
            {slotTitle}
          </Typography>
        )}
      </div>
    );
  }, [allDay, slotTitle, selected, groupTitle]);

  const dispatchVolunteerInfo = useCallback(() => {
    const filledAnswers = answers.filter((a) => a.answer !== '');
    const updatedSignups = selectedSignups.map((signup) =>
      isSpecificSignup(signup, selected)
        ? {
            ...signup,
            comment: volunteerComment,
            answers: filledAnswers,
          }
        : signup
    );

    dispatch({
      type: SET_SELECTED_SIGNUPS,
      payload: updatedSignups,
    });
  }, [volunteerComment, answers, selectedSignups, selected, dispatch]);

  const signupQuestions = useMemo(
    () =>
      questions
        .filter(
          (question) => question.display_on_signup && question?.applicable_to?.includes(sheetType)
        )
        .map((q) => {
          const currentAnswer = answers?.find((i) => i.question_id === q.uuid)?.answer || '';
          const showError = q.is_required && hasSubmitted && currentAnswer === '';

          return (
            <Input
              key={q.uuid}
              label={q.title}
              value={currentAnswer}
              onChange={(e) => handleAnswerChange(q.uuid, e.target.value)}
              onBlur={dispatchVolunteerInfo}
              placeholder={q.description}
              required={q.is_required}
              error={showError && questionError}
              data-qa-id="sign-up-overlay-question-input"
            />
          );
        }),
    [
      questions,
      sheetType,
      answers,
      questionError,
      hasSubmitted,
      dispatchVolunteerInfo,
      handleAnswerChange,
    ]
  );

  return (
    <>
      <div className="sign-up-overlay__content-row">
        <div
          className={cx('sign-up-overlay__item', {
            'sign-up-overlay__item--error': errorMsg,
          })}
        >
          <div className="sign-up-overlay__item-row">
            <div>
              {signupTitle}
              {!legacyDateTime && slotDescription && (
                <Typography
                  variant="paragraph3"
                  data-qa-id="item-description"
                  color="shadow"
                  className="signup-sheet__rendered-rich-text"
                >
                  {sanitizeString(slotDescription)}
                </Typography>
              )}
            </div>
            {!legacyDateTime && (
              <VolunteerSignupOverlayCounter
                selectedItemInSlot={selected}
                selectedSignups={selectedSignups}
                selectedSignupsToShow={selectedSignupsToShow}
                updateSelectedSignupsToShow={updateSelectedSignupsToShow}
                adjustQuantity={adjustQuantity}
                currentUserId={currentUserId}
              />
            )}
          </div>
          <VolunteerLinkPreview
            uuid={slotId ?? signupOption}
            productLink={productLink}
            isOrganizer={isOrganizer}
            organizerName={organizerName}
            isDesktop={isDesktop}
            disabled
            compact
            showDisclaimerPopover={false}
          />
          {signupQuestions}
          <Input
            inputClassName="signup-sheets__text-textarea text__textarea"
            data-qa-id="sign-up-overlay-item-comment"
            type="textarea"
            label="Comment"
            value={volunteerComment || ''}
            onChange={(e) => setVolunteerComment(e.target.value)}
            onBlur={dispatchVolunteerInfo}
            placeholder="Add a comment (optional)"
            rows={1}
            maxLength={signupCommentMaxLength}
            ref={commentRef}
            onInput={() => resizeTextarea(commentRef.current)}
          />
        </div>
        <Button
          variant="transparent"
          className="sign-up-overlay__item-remove-btn"
          onClick={() => handleTrashCanClick(selected)}
          data-qa-id="sign-up-overlay-item-remove-btn"
        >
          <TrashIcon ratio={0.75} color={pebble} />
        </Button>
      </div>
      {errorMsg && (
        <Typography color="mexicanOpalWebText" variant="label3">
          {errorMsg}
        </Typography>
      )}
    </>
  );
};
